import React, { useRef, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import Close from '@mui/icons-material/Close';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import * as XLSX from 'xlsx';
import { AppButton } from '../../components/UI';

// Reads the first sheet of an .xlsx file: header row + one object per data row.
// Returns the rows and the required headers that are missing.
export const parseXlsx = async (file, requiredHeaders, cleanRow = (r) => r) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data);
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', blankrows: false });
  const headers = (jsonData[0] || []).map((h) => String(h).trim());
  const rows = jsonData.slice(1).map((r) => cleanRow(r.reduce((acc, x, i) => {
    if (headers[i]) acc[headers[i]] = x;
    return acc;
  }, {})));
  return { rows, missing: requiredHeaders.filter((h) => !headers.includes(h)) };
};

// Builds and downloads an empty .xlsx with the expected header row.
export const downloadTemplate = (headers, fileName) => {
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  ws['!cols'] = headers.map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Import');
  XLSX.writeFile(wb, fileName);
};

const formatSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} Ko` : `${(bytes / (1024 * 1024)).toFixed(1)} Mo`);

export const ImportStepper = ({ steps, current, errorStep }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
    {steps.map((label, i) => {
      const done = i < current;
      const active = i === current;
      const error = errorStep === i;
      const color = error ? '#b91c1c' : done ? '#16a34a' : active ? '#03407b' : '#cbd5e1';
      return (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flex: '0 0 auto', width: 150 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 800, color: done || active || error ? '#fff' : '#94a3b8',
              background: done || active || error ? color : '#f1f5f9', border: `2px solid ${color}`,
              boxShadow: active ? '0 0 0 4px #ebf4fb' : 'none', transition: 'all 0.2s ease',
            }}>
              {done ? <CheckCircle sx={{ fontSize: 18 }} /> : error ? <ErrorOutlined sx={{ fontSize: 18 }} /> : i + 1}
            </Box>
            <Typography sx={{ fontSize: '0.8rem', textAlign: 'center', fontWeight: active ? 700 : 500, color: active ? '#0f172a' : '#64748b', lineHeight: 1.25 }}>
              {label}
            </Typography>
          </Box>
          {i < steps.length - 1 && (
            <Box sx={{ flex: 1, height: 2, mt: '15px', mx: -4, background: i < current ? '#16a34a' : '#e2e8f0', borderRadius: 1 }} />
          )}
        </React.Fragment>
      );
    })}
  </Box>
);

// Step 2: drop zone + expected columns + template download.
export const ImportFileStep = ({ file, onFile, requiredHeaders, templateName, entityLabel, error }) => {
  const input = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) return onFile(null, 'Le fichier doit être au format Excel (.xlsx).');
    onFile(f);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <input ref={input} type="file" accept=".xlsx" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />

      {file ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, borderRadius: '12px', background: error ? '#fef2f2' : '#f0fdf4', border: `1px solid ${error ? '#fca5a5' : '#86efac'}` }}>
          <TableChartOutlined sx={{ color: error ? '#b91c1c' : '#16a34a', fontSize: 30 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{formatSize(file.size)} · le contenu sera vérifié à l'étape suivante</Typography>
          </Box>
          <Tooltip title="Changer de fichier">
            <IconButton size="small" onClick={() => onFile(null)} sx={{ color: '#64748b', '&:hover': { color: '#b91c1c', background: '#fef2f2' } }}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => input.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 4, px: 2, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
            border: `2px dashed ${error ? '#fca5a5' : dragging ? '#03407b' : '#cbd5e1'}`,
            background: dragging ? '#ebf4fb' : '#f8fafc', transition: 'all 0.15s ease',
            '&:hover': { borderColor: '#03407b', background: '#f0f7fd' },
            '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
          }}
        >
          <CloudUploadOutlined sx={{ fontSize: 40, color: dragging ? '#03407b' : '#7fb3d3' }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
            {dragging ? 'Déposez le fichier ici' : `Glissez le fichier Excel des ${entityLabel} ici`}
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>ou <b style={{ color: '#03407b' }}>cliquez pour le choisir</b> · format .xlsx uniquement</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.25, borderRadius: '10px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.84rem' }}>
          <ErrorOutlined sx={{ fontSize: 18, mt: 0.1 }} /> <span>{error}</span>
        </Box>
      )}

      <Box sx={{ p: 1.75, borderRadius: '12px', background: '#fff', border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>
            Colonnes obligatoires dans la 1<sup>re</sup> ligne du fichier
          </Typography>
          <AppButton variant="ghost" size="small" startIcon={<DownloadOutlined fontSize="small" />} onClick={() => downloadTemplate(requiredHeaders, templateName)}>
            Télécharger le modèle Excel
          </AppButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {requiredHeaders.map((h) => (
            <Chip key={h} label={h} size="small" sx={{ height: 22, fontFamily: 'monospace', fontSize: '0.72rem', color: '#03407b', background: '#ebf4fb' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Step 3 banner: what is about to be imported, and where.
export const ImportSummary = ({ count, entityLabel, targetLabel, targetName, emptyEmails }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.5, px: 1.75, py: 1.25, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1' }}>
    <CheckCircle sx={{ color: '#03407b', fontSize: 20 }} />
    <Typography sx={{ fontSize: '0.88rem', color: '#03407b' }}>
      <b>{count} {entityLabel}</b> seront importés dans {targetLabel} <b>{targetName}</b>. Vérifiez la liste et retirez les lignes à ne pas importer.
    </Typography>
    {emptyEmails > 0 && (
      <Chip size="small" label={`${emptyEmails} sans email`} sx={{ ml: 'auto', height: 22, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d' }} />
    )}
  </Box>
);
